import React from "react";
import {
  Header,
  SpaceBetween,
  ColumnLayout,
} from "@amzn/awsui-components-react";
import { CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME } from "models/generateCustomAttributesSchema";
import { GetScanResultResponse } from "pages/draft-overview/definitions";
import { IGaiaobjectNameProps } from "components/gaiaobject-name/definitions";
import DynamicFormField from "components/dynamic-form-field";
import { removeCommasFromNumberString } from "components/monetary-input/definitions";
import styles from "./style.module.scss";
import {
  getEntityCodeFromLongName,
  buildEntityCompositeKey,
} from "features/ui-content/utils";

export function formatTimestamp(
  timestamp: string | undefined,
): string | undefined {
  if (!timestamp) return undefined;

  const num = Number.parseInt(timestamp, 10);
  if (Number.isNaN(num)) return undefined;

  const date = new Date(num * 1000);
  return date.toISOString().split("T")[0];
}

/**
 * Formats a suffix value based on field type
 * Handles date/period fields with special formatting
 */
export function formatSuffixValue(value: any, fieldName: string): string {
  if (!value) return "";

  if (
    fieldName.toLowerCase().includes("date") ||
    fieldName.toLowerCase().includes("period")
  ) {
    const isNumeric =
      typeof value === "number" ||
      (typeof value === "string" && /^\d+$/.test(value));

    if (isNumeric) {
      const numValue =
        typeof value === "string" ? Number.parseInt(value, 10) : value;
      const date = new Date(numValue * 1000);
      if (!Number.isNaN(date.getTime())) {
        return date.toISOString().split("T")[0].replace(/-/g, "/");
      }
    }

    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return date.toISOString().split("T")[0].replace(/-/g, "/");
    }
  }

  return String(value);
}

export function validateValueInOptions(
  value: any,
  options: any[] = [],
): boolean {
  return options.some((o) =>
    (Array.isArray(o.options) ? o.options : [o]).some(
      (opt: any) => opt.value === value || opt.label === value,
    ),
  );
}

export function processGovernmentEntityValue(
  country: string | undefined,
  value: any,
  uiContent: any,
): any | undefined {
  if (!value) return value;

  const govOptions = uiContent?.governmentEntityOptions ?? [];
  const optionsList = govOptions
    .flatMap((group: any) => group.options ?? [])
    .filter((o: any) => {
      if (!country) return true;
      return o.country === country;
    });

  // First, check if the value is already a valid option value
  for (const obj of optionsList) {
    const optsArray = Array.isArray(obj.options) ? obj.options : [obj];
    const matchValue = optsArray.find((opt: any) => opt.value === value);
    if (matchValue) {
      return matchValue.value;
    }
  }

  // If not found by value, check if it matches a label
  for (const obj of optionsList) {
    const optsArray = Array.isArray(obj.options) ? obj.options : [obj];
    const matchLabel = optsArray.find((opt: any) => opt.label === value);
    if (matchLabel) {
      return matchLabel.value;
    }
  }

  return undefined;
}

export function processFieldValue(
  fieldName: string,
  value: any,
  customAttributes: any[] = [],
  uiContent?: any,
  filteredOptions?: any[],
): any {
  if (!value) return value;

  const fieldSchema = customAttributes.find(
    (a) => a.attributeName === fieldName,
  );
  if (!fieldSchema) return value;

  const { formFieldType, dropdownValueList } = fieldSchema;
  const optionsKey = `${fieldName}Options`;
  const optionsList =
    filteredOptions ?? uiContent?.[optionsKey] ?? dropdownValueList ?? [];

  if (formFieldType === "Multiselect") {
    const arrayValue = Array.isArray(value)
      ? value
      : String(value)
          .split(",")
          .map((s) => s.trim());

    if (optionsList.length > 0) {
      const valid = arrayValue.filter((v) =>
        validateValueInOptions(v, optionsList),
      );
      return valid.length > 0 ? valid : undefined;
    }
    return arrayValue;
  }

  if (["Dropdown", "RadioGroup"].includes(formFieldType)) {
    return optionsList.length > 0 && validateValueInOptions(value, optionsList)
      ? value
      : undefined;
  }

  return value;
}

export function transformScanResultToFormData(
  scanResult: GetScanResultResponse["scanResult"],
  customAttributes: any[],
  uiContent?: any,
  owner?: string,
) {
  const entityValue = processFieldValue(
    "entity",
    scanResult.entity,
    customAttributes,
    uiContent,
  );
  const country = processFieldValue(
    "country",
    scanResult.country,
    customAttributes,
    uiContent,
  );

  // Look up entity code and reconstruct composite key for dropdown matching
  const entityCodeFromName = entityValue
    ? getEntityCodeFromLongName(entityValue, uiContent)
    : "";

  const customAttributesData: any = {
    owner: owner,
    country: country,
    governmentEntity: processGovernmentEntityValue(
      country,
      scanResult.governmentEntity,
      uiContent,
    ),
    localJurisdiction: processFieldValue(
      "localJurisdiction",
      scanResult.localJurisdiction,
      customAttributes,
      uiContent,
    ),
    entity: entityCodeFromName
      ? buildEntityCompositeKey(entityValue, entityCodeFromName)
      : entityValue,
    entityCode: entityCodeFromName,
    noticeType: processFieldValue(
      "noticeType",
      scanResult.noticeType,
      customAttributes,
      uiContent,
    ),
    noticeDate: processFieldValue(
      "noticeDate",
      formatTimestamp(scanResult.noticeDate),
      customAttributes,
      uiContent,
    ),
    taxType: processFieldValue(
      "taxType",
      scanResult.taxType,
      customAttributes,
      uiContent,
    ),
    severity: processFieldValue(
      "severity",
      scanResult.severity,
      customAttributes,
      uiContent,
    ),
    description: processFieldValue(
      "description",
      scanResult.description,
      customAttributes,
      uiContent,
    ),
    taxAuthority: processFieldValue(
      "taxAuthority",
      scanResult.taxAuthority,
      customAttributes,
      uiContent,
    ),
    taxPeriodStart: processFieldValue(
      "taxPeriodStart",
      formatTimestamp(scanResult.taxPeriodStart),
      customAttributes,
      uiContent,
    ),
    taxPeriodEnd: processFieldValue(
      "taxPeriodEnd",
      formatTimestamp(scanResult.taxPeriodEnd),
      customAttributes,
      uiContent,
    ),
    registrationNumber: processFieldValue(
      "registrationNumber",
      scanResult.registrationNumber,
      customAttributes,
      uiContent,
    ),
    actualDueDate: processFieldValue(
      "actualDueDate",
      formatTimestamp(scanResult.actualDueDate),
      customAttributes,
      uiContent,
    ),
    ResponseDeadline: processFieldValue(
      "ResponseDeadline",
      formatTimestamp(scanResult.ResponseDeadline),
      customAttributes,
      uiContent,
    ),
    noticeId: processFieldValue(
      "noticeId",
      scanResult.noticeId,
      customAttributes,
      uiContent,
    ),
    Form: processFieldValue(
      "Form",
      scanResult.Form,
      customAttributes,
      uiContent,
    ),
    labels: processFieldValue(
      "labels",
      scanResult.Labels,
      customAttributes,
      uiContent,
    ),
    closeDate: processFieldValue(
      "closeDate",
      formatTimestamp(scanResult.closeDate),
      customAttributes,
      uiContent,
    ),
    totalTaxAmount: processFieldValue(
      "totalTaxAmount",
      scanResult.total_tax_amount
        ? removeCommasFromNumberString(scanResult.total_tax_amount)
        : scanResult.total_tax_amount,
      customAttributes,
      uiContent,
    ),
    totalPenaltyAmount: processFieldValue(
      "totalPenaltyAmount",
      scanResult.total_penalty_amount
        ? removeCommasFromNumberString(scanResult.total_penalty_amount)
        : scanResult.total_penalty_amount,
      customAttributes,
      uiContent,
    ),
    totalInterestAmount: processFieldValue(
      "totalInterestAmount",
      scanResult.total_interest_amount
        ? removeCommasFromNumberString(scanResult.total_interest_amount)
        : scanResult.total_interest_amount,
      customAttributes,
      uiContent,
    ),
    totalOtherAmount: processFieldValue(
      "totalOtherAmount",
      scanResult.total_other_amount
        ? removeCommasFromNumberString(scanResult.total_other_amount)
        : scanResult.total_other_amount,
      customAttributes,
      uiContent,
    ),
    status: scanResult.status || undefined,
  };

  // Calculate titleSuffix from backend data if suffixField is configured
  let titleSuffix = "";
  if (uiContent?.suffixField) {
    const suffixSourceValue =
      scanResult[uiContent.suffixField as keyof typeof scanResult] ||
      customAttributesData[uiContent.suffixField];

    if (
      suffixSourceValue &&
      suffixSourceValue !== "-" &&
      suffixSourceValue !== "N/A"
    ) {
      titleSuffix = formatSuffixValue(suffixSourceValue, uiContent.suffixField);
    }
  }

  return {
    titleSuffix,
    [CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME]: customAttributesData,
  };
}

export function buildGaiaObjectProps(
  watchedFields: {
    country: string;
    governmentEntity: string;
    localJurisdiction: string;
    entity: string;
    noticeType: string;
    taxPeriodStartDate: string;
    taxPeriodEndDate: string;
    litigationType: string;
    taxTypes: string[];
  },
  type: string,
  titleSuffix: string,
  entityCode?: string,
): IGaiaobjectNameProps {
  return {
    country: watchedFields.country,
    governmentEntity: watchedFields.governmentEntity,
    localJurisdiction: watchedFields.localJurisdiction,
    entity: watchedFields.entity,
    noticeType: watchedFields.noticeType,
    taxPeriodStartDate: watchedFields.taxPeriodStartDate,
    taxPeriodEndDate: watchedFields.taxPeriodEndDate,
    litigationType: watchedFields.litigationType,
    category: type,
    titleSuffix,
    entityCode: entityCode,
    taxTypes: watchedFields.taxTypes,
  };
}

export function buildFormResetData(transformedObject: any) {
  return {
    titleSuffix: transformedObject?.titleSuffix ?? "",
    country: transformedObject?.country ?? "",
    owner: transformedObject?.owner ?? "",
    [CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME]:
      transformedObject?.[CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME] ?? {},
  };
}

export function buildDraftFormResetData(formData: any) {
  return {
    titleSuffix: "",
    owner: "",
    ...formData,
  };
}

export const extractUniqueGroups = (customAttributes: any[]): string[] => {
  const uniqueGroups: string[] = [];
  customAttributes.forEach((attr: any) => {
    const group = attr?.group || "";
    if (!uniqueGroups.includes(group)) {
      uniqueGroups.push(group);
    }
  });
  return uniqueGroups;
};

export const groupAttributesForRendering = (attributes: any[]): any[][] => {
  const groups: any[][] = [];
  let i = 0;

  while (i < attributes.length) {
    const currentAttr = attributes[i];

    if (currentAttr.singleLine) {
      groups.push([currentAttr]);
      i++;
    } else {
      const columnFields: any[] = [];
      while (i < attributes.length && !attributes[i].singleLine) {
        columnFields.push(attributes[i]);
        i++;
      }
      groups.push(columnFields);
    }
  }

  return groups;
};

/**
 * Centralized error handling for GaiaObject operations (notices, litigations, etc.)
 * @param error - The error object from the API call
 * @param category - The category of the GaiaObject (e.g., "notice", "litigation")
 * @param defaultMessage - The default message to show for non-duplicate errors
 * @returns The appropriate error message to display to the user
 */
export const getGaiaObjectErrorMessage = (
  error: any,
  category: string,
  defaultMessage: string,
): string => {
  const backendMessage = error?.data?.message || "";
  const isDuplicateError = backendMessage.includes(
    `Duplicate ${category} found`,
  );

  return isDuplicateError
    ? `Error - duplicate ${category} exists.`
    : defaultMessage;
};

export const AttributeGroupRenderer: React.FC<{
  groupName: string;
  groupAttributes: any[];
  formState: any;
  control: any;
  type: string;
  mode: string;
  setValue: any;
  filteredGovEntityOptions: any[];
}> = ({
  groupName,
  groupAttributes,
  formState,
  control,
  type,
  mode,
  setValue,
  filteredGovEntityOptions,
}) => {
  const groupedAttributes = groupAttributesForRendering(groupAttributes);

  return (
    <div className={styles.horizontalDivider}>
      <Header variant="h3">{groupName}</Header>
      <SpaceBetween size="l">
        {groupedAttributes.map((group, groupIndex) => {
          if (group.length === 1 && group[0].singleLine) {
            const attr = group[0];

            if (attr.fieldName === "Description") {
              return (
                <div key={attr.attributeName}>
                  <DynamicFormField
                    attributeSchema={attr}
                    formState={formState}
                    control={control}
                    namePrefix={CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME}
                    gaiaObjectType={type}
                    mode={mode}
                  />
                </div>
              );
            }
            return (
              <ColumnLayout key={attr.attributeName} columns={2}>
                <DynamicFormField
                  attributeSchema={attr}
                  formState={formState}
                  control={control}
                  namePrefix={CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME}
                  gaiaObjectType={type}
                  mode={mode}
                  setValue={setValue}
                />
                <div />
              </ColumnLayout>
            );
          }
          return (
            <ColumnLayout
              key={`column-group-${groupIndex}`}
              columns={groupName === "" ? 1 : 2}
            >
              {group.map((attributeSchema: any) => (
                <React.Fragment key={attributeSchema.attributeName}>
                  <DynamicFormField
                    attributeSchema={attributeSchema}
                    formState={formState}
                    control={control}
                    namePrefix={CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME}
                    gaiaObjectType={type}
                    filteredOptions={
                      attributeSchema.attributeName === "governmentEntity"
                        ? filteredGovEntityOptions
                        : undefined
                    }
                    mode={mode}
                    setValue={setValue}
                  />
                </React.Fragment>
              ))}
            </ColumnLayout>
          );
        })}
      </SpaceBetween>
    </div>
  );
};
